import moment from 'moment';
import { FinancialStatementPeriodFullWrite } from 'api/models/FinancialStatementPeriodFullWrite';
import { getArrayValue } from 'utilities';

export type NormalizableDate = Date | string | null | undefined;

/**
 * Normalize values commonly coming from APIs/localStorage into a valid Date instance (or null).
 * - Accepts Date or ISO string
 * - Returns null for invalid / empty values
 */
export const normalizeDate = (value: NormalizableDate): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Stable ISO key for Date/string inputs (useful for memoization keys).
 */
export const dateToISOStringKey = (value: NormalizableDate): string | null => {
  const d = normalizeDate(value);
  return d ? d.toISOString() : null;
};

export const isDateInPeriods = (
  date: string,
  periods: FinancialStatementPeriodFullWrite[],
  dateField: keyof FinancialStatementPeriodFullWrite,
  dateFormat: string
) => {
  const formattedDate = moment(date).format(dateFormat);
  const dates = getArrayValue(periods?.map(period => period?.[dateField]));
  return dates.includes(formattedDate);
};

export const isLastQuarter = (asOfDate: string, fyEnd: string) => {
  const date = moment(asOfDate, moment.ISO_8601, true);
  const fyEndMoment = moment(fyEnd, moment.ISO_8601, true);
  const fyEndStartMoment = fyEndMoment.clone().subtract(3, 'months');
  const dateIsWithinLastQuarter = date.isBetween(fyEndStartMoment, fyEndMoment, null, '[]');

  return dateIsWithinLastQuarter;
};

/**
 * deeply compares two date-like values (Date instance, ISO string, or null).
 * Returns true if they represent the same time.
 */
export const areDatesEqual = (
  a: NormalizableDate, 
  b: NormalizableDate
): boolean => {
  const dateA = normalizeDate(a);
  const dateB = normalizeDate(b);

  if (!dateA && !dateB) {
    return true;
  }
  if (!dateA || !dateB) {
    return false;
  }

  return dateA.getTime() === dateB.getTime();
};